import { useState } from 'react'
import { Crown, LogOut, UserPlus, X } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { displayName, useSocialStore } from '@/store/social'
import { useAppStore } from '@/store/app'
import { useSelfStatus } from '@/store/presence'
import { isOnline } from '@/lib/presence'
import { avatarUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import type { Channel, User } from '@/types/api'
import { Avatar } from '../Avatar'
import { FriendPicker } from '../home/FriendPicker'
import { Modal } from '../settings/Modal'
import member from '../MemberList.module.css'
import styles from './GroupPanel.module.css'
import { StyledName } from '../StyledName'
import { publicUser } from '@/lib/auth'
import { userCardHandler, userContextHandler } from '@/store/contextMenu'

const MAX_GROUP = 10
const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/** Правая колонка группового ЛС: участники, добавление друзей, выход. */
export function GroupPanel({ channel }: { channel: Channel }) {
  const me = useAuthStore((s) => s.user)
  const presences = useSocialStore((s) => s.presences)
  const selfStatus = useSelfStatus()
  const addToGroup = useSocialStore((s) => s.addToGroup)
  const removeFromGroup = useSocialStore((s) => s.removeFromGroup)
  const closeDM = useSocialStore((s) => s.closeDM)
  const setView = useAppStore((s) => s.setView)
  const [adding, setAdding] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!me) return null
  const owner = channel.owner_id === me.id
  const members: User[] = [publicUser(me), ...(channel.recipients ?? [])]
  members.sort((a, b) => Number(b.id === channel.owner_id) - Number(a.id === channel.owner_id) || displayName(a).localeCompare(displayName(b), 'ru'))
  const canAdd = members.length < MAX_GROUP

  const leave = async () => {
    setBusy(true)
    try {
      await closeDM(channel.id)
      setView({ kind: 'home', page: 'friends' })
    } finally {
      setBusy(false)
      setLeaving(false)
    }
  }

  return (
    <aside className={cx(member.list, styles.panel, 'scroller')} aria-label="Участники группы">
      <div className={member.wco} aria-hidden="true" />
      <section>
        <h3 className={member.group}>Участники — {members.length}</h3>
        {members.map((u) => {
          const status = u.id === me.id ? selfStatus : (presences[u.id] ?? 'offline')
          const online = u.id === me.id || isOnline(status)
          return (
            // как в списке участников сервера: нажатие — карточка, правая кнопка — меню
            <div
              key={u.id}
              className={cx(member.member, styles.row)}
              data-offline={online ? undefined : ''}
              role="button"
              tabIndex={0}
              data-tooltip={`@${u.username}`}
              onClick={userCardHandler(u.id, { user: u, dmChannelId: channel.id })}
              onContextMenu={userContextHandler(u.id, { user: u, dmChannelId: channel.id })}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return
                const r = e.currentTarget.getBoundingClientRect()
                userCardHandler(u.id, { user: u, dmChannelId: channel.id })({ preventDefault: () => e.preventDefault(), clientX: r.left, clientY: r.top })
              }}
            >
              <Avatar initial={displayName(u).slice(0, 1).toUpperCase()} color={colorOf(u.id)} size={32} status={u.id === me.id || isOnline(status) ? status : 'offline'} src={avatarUrl(u.id, u.avatar)} decoration={u.avatar_decoration_data} />
              <span className={member.text}>
                <span className={member.name}><StyledName name={displayName(u)} style={u.display_name_styles} className={member.nameText} /></span>
                <span className={member.activity}>@{u.username}</span>
              </span>
              {u.id === channel.owner_id ? <Crown size={14} className={member.crown} aria-label="Владелец группы" /> : null}
              {owner && u.id !== me.id ? (
                <button type="button" className={styles.remove} data-tooltip="Исключить из группы" aria-label={`Исключить ${displayName(u)}`} onClick={(e) => { e.stopPropagation(); void removeFromGroup(channel.id, u.id) }}>
                  <X size={16} />
                </button>
              ) : null}
            </div>
          )
        })}
      </section>
      <div className={styles.actions}>
        <button type="button" className={styles.action} disabled={!canAdd} data-tooltip={canAdd ? 'Добавить друзей' : 'В группе не больше 10 участников'} onClick={() => setAdding(true)}>
          <UserPlus size={18} /> Добавить друзей
        </button>
        <button type="button" className={cx(styles.action, styles.danger)} onClick={() => setLeaving(true)}>
          <LogOut size={18} /> Покинуть группу
        </button>
      </div>
      {adding ? (
        <FriendPicker
          title="Добавить друзей в группу"
          submitLabel={(n) => (n ? `Добавить (${n})` : 'Добавить')}
          exclude={members.map((u) => u.id)}
          max={MAX_GROUP - members.length}
          onSubmit={async (ids) => {
            for (const id of ids) await addToGroup(channel.id, id)
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {leaving ? (
        <Modal title="Покинуть группу" onClose={() => setLeaving(false)}>
          <p className={styles.confirmText}>Вы уверены, что хотите покинуть группу? Вернуться можно будет только по приглашению участника.</p>
          <div className={styles.confirmButtons}>
            <button type="button" className={styles.cancel} onClick={() => setLeaving(false)}>Отмена</button>
            <button type="button" className={styles.confirm} disabled={busy} onClick={() => void leave()}>Покинуть группу</button>
          </div>
        </Modal>
      ) : null}
    </aside>
  )
}
